import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import api, { formatError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { CheckCircle2, XCircle, Trash2, Megaphone, IndianRupee, Phone, MapPin, Clock } from "lucide-react";

const TABS = [
  { key: "pending", label: "अनुमोदन बाकी" },
  { key: "approved", label: "अनुमोदित" },
  { key: "rejected", label: "अस्वीकृत" },
  { key: "", label: "सभी" },
];

export default function ClassifiedsAdminPage() {
  const [tab, setTab] = useState("pending");
  const [items, setItems] = useState([]);
  const [stats, setStats] = useState({ donation: { count: 0, total: 0 }, classified: { count: 0, total: 0 } });
  const [loading, setLoading] = useState(false);

  const load = () => {
    setLoading(true);
    const params = tab ? { status: tab } : {};
    Promise.all([
      api.get("/admin/classifieds", { params }),
      api.get("/admin/payments/stats"),
    ]).then(([r, s]) => { setItems(r.data); setStats(s.data); }).catch(() => {}).finally(() => setLoading(false));
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [tab]);

  const moderate = async (id, admin_status) => {
    try {
      await api.patch(`/admin/classifieds/${id}`, { admin_status });
      toast.success(admin_status === "approved" ? "प्रकाशित" : "अस्वीकृत");
      load();
    } catch (e) { toast.error(formatError(e)); }
  };
  const remove = async (id) => {
    if (!window.confirm("इस विज्ञापन को हटा दें?")) return;
    try { await api.delete(`/admin/classifieds/${id}`); toast.success("हटा दिया"); load(); }
    catch (e) { toast.error(formatError(e)); }
  };

  return (
    <div data-testid="admin-classifieds-page">
      <div className="mb-6 flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-devnagri-heading text-3xl font-black text-[#0F172A]">विज्ञापन प्रबंधन</h1>
          <p className="text-sm text-slate-500 mt-1">Paid classifieds — अनुमोदन / अस्वीकृति / राजस्व</p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <StatCard label="विज्ञापन" count={stats.classified.count} total={stats.classified.total} color="orange"/>
          <StatCard label="दान" count={stats.donation.count} total={stats.donation.total} color="emerald"/>
        </div>
      </div>

      <div className="flex gap-2 mb-6 border-b border-slate-200 overflow-x-auto">
        {TABS.map((t) => (
          <button key={t.key || "all"} onClick={() => setTab(t.key)}
            className={`px-4 py-2 text-sm font-medium border-b-2 whitespace-nowrap ${
              tab === t.key ? "border-[#0F172A] text-[#0F172A]" : "border-transparent text-slate-500 hover:text-slate-700"
            }`}
            data-testid={`cls-tab-${t.key || "all"}`}>
            {t.label}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="text-slate-500 py-8 text-center">लोड हो रहा है…</div>
      ) : items.length === 0 ? (
        <div className="text-slate-500 py-16 text-center bg-white rounded-lg border border-dashed border-slate-200">
          <Megaphone className="w-10 h-10 mx-auto mb-3 text-slate-300"/>इस श्रेणी में कोई विज्ञापन नहीं
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {items.map((c) => (
            <div key={c.id} className={`bg-white border-2 rounded-lg p-4 ${
              c.package_id === "premium" ? "border-amber-400" : c.package_id === "featured" ? "border-orange-300" : "border-slate-200"
            }`} data-testid={`admin-classified-${c.id}`}>
              <div className="flex items-start gap-3">
                {c.image_url && <img src={c.image_url} alt="" className="w-20 h-20 rounded object-cover shrink-0"/>}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap mb-1">
                    <span className={`text-[10px] uppercase tracking-widest font-bold px-2 py-0.5 rounded ${
                      c.package_id === "premium" ? "bg-amber-100 text-amber-800" :
                      c.package_id === "featured" ? "bg-orange-100 text-orange-800" :
                      "bg-slate-100 text-slate-700"
                    }`}>{c.package_id}</span>
                    <span className="text-emerald-700 text-sm font-bold">₹{c.amount}</span>
                    <span className="text-[10px] text-slate-500">{c.category}</span>
                  </div>
                  <h4 className="font-bold text-[#0F172A] leading-tight">{c.title}</h4>
                  <p className="text-xs text-slate-600 mt-1 line-clamp-2">{c.description}</p>
                  <div className="flex items-center gap-3 text-[10px] text-slate-500 mt-2 flex-wrap">
                    <span className="flex items-center gap-1"><Phone className="w-3 h-3"/>{c.phone}</span>
                    {c.area && <span className="flex items-center gap-1"><MapPin className="w-3 h-3"/>{c.area}</span>}
                    <span className="flex items-center gap-1"><Clock className="w-3 h-3"/>{new Date(c.created_at).toLocaleDateString("hi-IN")}</span>
                  </div>
                </div>
              </div>
              <div className="flex gap-2 mt-3 pt-3 border-t border-slate-100">
                {c.admin_status !== "approved" && (
                  <Button size="sm" onClick={() => moderate(c.id, "approved")} className="bg-emerald-600 hover:bg-emerald-700 text-white h-8 flex-1" data-testid={`cls-approve-${c.id}`}>
                    <CheckCircle2 className="w-3.5 h-3.5 mr-1"/>प्रकाशित करें
                  </Button>
                )}
                {c.admin_status !== "rejected" && (
                  <Button size="sm" variant="outline" onClick={() => moderate(c.id, "rejected")} className="h-8" data-testid={`cls-reject-${c.id}`}>
                    <XCircle className="w-3.5 h-3.5"/>
                  </Button>
                )}
                <Button size="sm" variant="outline" onClick={() => remove(c.id)} className="h-8 text-red-600 border-red-200 hover:bg-red-50" data-testid={`cls-del-${c.id}`}>
                  <Trash2 className="w-3.5 h-3.5"/>
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function StatCard({ label, count, total, color }) {
  const map = { orange: "bg-orange-50 text-orange-800 border-orange-200", emerald: "bg-emerald-50 text-emerald-800 border-emerald-200" };
  return (
    <div className={`px-4 py-2 rounded-md border ${map[color]}`}>
      <div className="text-[10px] uppercase tracking-widest font-bold">{label}</div>
      <div className="flex items-baseline gap-2">
        <IndianRupee className="w-3.5 h-3.5"/>
        <span className="text-lg font-black">{total.toLocaleString("hi-IN")}</span>
        <span className="text-xs opacity-70">({count})</span>
      </div>
    </div>
  );
}
